import { Fragment, useEffect, useState, useContext } from 'react';
import { useSelector, useDispatch, connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { EditIcon, AddIcon } from 'foundations-assets';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { List } from 'foundations-components/transitional/components/List';
import { H2 } from 'foundations-components/transitional/components/H2';
import FollowupEmailSelect from 'ContentComponents/cmv2/form/formAutomation/followupEmail/FollowupEmailSelect';
import WorkflowLink from 'ContentComponents/cmv2/form/formAutomation/connectedWorkflows/WorkflowLink';
import EmailEditorPanel from 'ContentComponents/cmv2/form/formAutomation/followupEmail/emailEditor/EmailEditorPanel';
import * as workflowsSelectors from 'ContentData/selectors/workflows';
import * as emailsSelectors from 'ContentData/selectors/emails';
import PickersContext from 'ContentComponents/context/PickersContext';
import { fetchEmailBuffer } from 'ContentData/actions/Emails';
import text from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapStateToProps = (state, props) => {
  const {
    formGuid,
    contentId
  } = props;
  return {
    workflowsThatSendEmails: workflowsSelectors.getWorkflowsThatSendEmails(state, {
      formGuid,
      contentId
    })
  };
};
const connector = connect(mapStateToProps);
const i18nKey = key => `ContentComponents.cmv2.fields.form.formWorkflowDependencies.followupEmailWorkflows.${key}`;
const FollowupEmailWorkflowModal = ({
  emailId: initialEmailId,
  formGuid,
  hasAutomatedEmailEditorAccess,
  hasInlineEmailEditorAccess,
  hasWorkflowsAppAccess,
  onClose,
  onConfirm,
  workflowsThatSendEmails
}) => {
  const dispatch = useDispatch();
  const [emailId, setEmailId] = useState(initialEmailId);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [isCreatingEmail, setIsCreatingEmail] = useState(false);
  const emailBuffers = useSelector(state => emailsSelectors.getEmailBuffers(state));
  useEffect(() => {}, [emailBuffers]);
  const emailBuffer = emailId && emailBuffers[emailId];
  const isInlineEditable = emailBuffer && emailBuffer.canBeEditedInEmbeddedEditor;
  const showUnpublishedError = useSelector(state => Boolean(emailId && emailsSelectors.getIsEmailUnpublished(state, {
    id: emailId
  })));
  const onCloseEmailEditor = newEmailId => {
    if (newEmailId && newEmailId !== emailId) {
      setEmailId(newEmailId);
    }
    setShowEmailEditor(false);
    setIsCreatingEmail(false);
  };
  const onSelectEmail = newEmailId => {
    setEmailId(newEmailId);
    if (newEmailId && !emailBuffers[newEmailId]) {
      dispatch(fetchEmailBuffer(newEmailId)).catch(err => {
        throw err;
      });
    }
  };
  const pickerComponents = useContext(PickersContext);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [showEmailEditor && /*#__PURE__*/_jsx(EmailEditorPanel, {
      emailId: !isCreatingEmail ? emailId : undefined,
      hasAutomatedEmailEditorAccess: hasAutomatedEmailEditorAccess,
      onClose: onCloseEmailEditor,
      pickerComponents: pickerComponents
    }), /*#__PURE__*/_jsxs(Modal, {
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: onClose
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.header')
          })
        })]
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [workflowsThatSendEmails.length > 0 && /*#__PURE__*/_jsxs(Alert, {
          "data-selenium": "form-followup-email-workflows-sending-emails-alert",
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.workflowsThatSendEmailsAlert.title')
          }),
          type: "warning",
          children: [/*#__PURE__*/_jsx(Text, {
            marginBlockEnd: "200",
            as: "p",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('modal.workflowsThatSendEmailsAlert.body1')
            })
          }), /*#__PURE__*/_jsx(List, {
            styled: true,
            marginBlockEnd: "200",
            wrapChildren: true,
            children: workflowsThatSendEmails.map(workflow => {
              return /*#__PURE__*/_jsx(WorkflowLink, {
                formGuid: formGuid,
                formWorkflowDependency: workflow,
                hasWorkflowsAppAccess: hasWorkflowsAppAccess
              }, workflow.flowId);
            })
          }), /*#__PURE__*/_jsx(Text, {
            margin: "0",
            as: "p",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('modal.workflowsThatSendEmailsAlert.body2')
            })
          })]
        }), /*#__PURE__*/_jsx(FormControl, {
          error: showUnpublishedError,
          help: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: i18nKey('modal.helpText_jsx'),
            options: {
              href: 'https://knowledge.hubspot.com/email/create-automated-emails-to-use-in-workflows'
            },
            elements: {
              Link
            }
          }),
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.emailSelect.label')
          }),
          required: true,
          validationMessage: showUnpublishedError && /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.unpublishedEmailError')
          }),
          children: /*#__PURE__*/_jsx(FollowupEmailSelect, {
            error: showUnpublishedError,
            hasAutomatedEmailEditorAccess: hasAutomatedEmailEditorAccess,
            hasInlineEmailEditorAccess: hasInlineEmailEditorAccess,
            onChange: onSelectEmail,
            onCreateEmail: () => {
              setShowEmailEditor(true);
              setIsCreatingEmail(true);
            },
            placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('modal.emailSelect.placeholder')
            }),
            value: emailId
          })
        }), hasInlineEmailEditorAccess && isInlineEditable && /*#__PURE__*/_jsx(View, {
          marginBlockStart: "200",
          as: "div",
          children: /*#__PURE__*/_jsx(Button, {
            "data-selenium": "form-followup-email-inline-edit-button",
            onClick: () => setShowEmailEditor(true),
            size: "small",
            use: "tertiary-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('modal.editEmail')
            })
          })
        })]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(LoadingButton, {
          disabled: !emailId || showUnpublishedError,
          onClick: () => onConfirm(emailId),
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.save')
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('modal.cancel')
          })
        })]
      })]
    })]
  });
};
const FollowupEmailWorkflowButton = ({
  followupEmailId,
  formGuid,
  hasAutomatedEmailEditorAccess,
  hasInlineEmailEditorAccess,
  hasWorkflowsAppAccess,
  onChangeFollowupEmailEvent,
  onFollowupEmailIdChange,
  workflowsThatSendEmails,
  readOnly = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isEditing = !!followupEmailId;
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const openModal = () => {
    setIsModalOpen(true);
  };
  const onConfirm = newEmailId => {
    if (isEditing && newEmailId === followupEmailId) {
      closeModal();
      return;
    }
    if (onChangeFollowupEmailEvent) {
      onChangeFollowupEmailEvent(isEditing ? 'form-followup-email-edit' : 'form-followup-email-add');
    }
    onFollowupEmailIdChange(newEmailId);
    closeModal();
    return;
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [isModalOpen && /*#__PURE__*/_jsx(FollowupEmailWorkflowModal, {
      emailId: followupEmailId,
      formGuid: formGuid,
      hasAutomatedEmailEditorAccess: hasAutomatedEmailEditorAccess,
      hasInlineEmailEditorAccess: hasInlineEmailEditorAccess,
      hasWorkflowsAppAccess: hasWorkflowsAppAccess,
      onClose: closeModal,
      onConfirm: onConfirm,
      workflowsThatSendEmails: workflowsThatSendEmails
    }), isEditing ? /*#__PURE__*/_jsx(IconButton, {
      "aria-label": text(i18nKey('editEmailTooltip')),
      disabled: readOnly,
      onClick: openModal,
      size: "xs",
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('editEmailTooltip')
      }),
      children: /*#__PURE__*/_jsx(EditIcon, {})
    }) : /*#__PURE__*/_jsx(Button, {
      disabled: readOnly,
      onClick: openModal,
      paddingInlineStart: "0",
      size: "xs",
      use: "transparent",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "100",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('buttonLabel')
        }), /*#__PURE__*/_jsx(AddIcon, {})]
      })
    })]
  });
};
export default connector(FollowupEmailWorkflowButton);